// Kleine stukken van het gezondheidsbord (spoor B, B7): getal, kleur, pil.
//
// Het oordeel (de kleur en het woord) komt altijd van de server. Hier staat
// alleen hoe het eruitziet. De kleuren zijn die van de bestaande beheerpillen
// (StatusPil, FundingNakijken): het bord verzint er geen nieuwe bij.

import type { CSSProperties, ReactNode } from "react";

import type { GezondheidsbordKleur, GezondheidsbordMeter } from "../../contracts";
import { color, radius, size, weight } from "../../tokens";

type Eenheid = GezondheidsbordMeter["eenheid"];

export const KLEUR: Record<GezondheidsbordKleur, { tekst: string; vlak: string }> = {
  groen: { tekst: color.accentGreen, vlak: "#ecfdf5" },
  oranje: { tekst: color.accentAmber, vlak: color.accentAmberBg },
  rood: { tekst: color.accentRed, vlak: "#fef2f2" },
  grijs: { tekst: color.soft, vlak: color.lineFaint },
};

/** "45,4%" of "226" — een streepje als er geen getal is, nooit een 0. */
export function getal(v: number | null | undefined, eenheid: Eenheid): string {
  if (v === null || v === undefined) return "–";
  const t = v.toLocaleString("nl-NL", { maximumFractionDigits: 1 });
  return eenheid === "%" ? `${t}%` : t;
}

/** "okt 2026" uit een periode (jjjj-mm-dd); null als er niets te lezen is. */
export function maand(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("nl-NL", { month: "short", year: "numeric" });
}

/** "Economie & Ondernemen" → "E&O" voor de smalle pillen; andere namen heel. */
export function kort(naam: string): string {
  return naam.includes(" & ")
    ? naam
        .split(" & ")
        .map((w) => w.charAt(0))
        .join("&")
    : naam;
}

export function Pil({
  kleur,
  children,
  title,
  style,
}: {
  kleur: GezondheidsbordKleur;
  children: ReactNode;
  title?: string;
  style?: CSSProperties;
}) {
  return (
    <span
      title={title}
      data-kleur={kleur}
      style={{
        display: "inline-block",
        background: KLEUR[kleur].vlak,
        color: KLEUR[kleur].tekst,
        borderRadius: radius.pill,
        padding: "2px 9px",
        fontSize: size.label,
        fontWeight: weight.medium,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </span>
  );
}

/** De tabelkop van de beheertabellen (zelfde maat als de meterkast). */
export const kop: CSSProperties = {
  fontSize: size.columnLabel,
  fontWeight: weight.medium,
  color: color.faint,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  textAlign: "left",
  padding: "9px 12px",
  background: color.surfaceAlt,
  verticalAlign: "bottom",
};

export const cijfer: CSSProperties = { fontVariantNumeric: "tabular-nums" };
